import type { Maybe, NonEmptyArray } from '@quipex/core/primitives';
import type {
    CurrentUserBuildingPermissionResponse,
    PermissionKey,
} from '@quipex/shared/contracts';

// --- Permission pattern typing ---
// Permission fragments that can be composed into permission patterns
// e.g. 'OrgUsersRead' (exact), '!OrgUsersRoleUpdate' (NOT exact)

/**
 * Exact string equality permission pattern.
 * @example 'OrgUsersRead'
 */
type ExactPermissionMatch = PermissionKey;

/**
 * NOT exact match permission pattern with negation prefix.
 * @example '!OrgUsersRoleUpdate' matches users who do NOT have the permission
 */
type NegationPermissionMatch = `!${PermissionKey}`;

/**
 * Combined permission pattern supporting exact match or negation.
 */
type PermissionPattern = ExactPermissionMatch | NegationPermissionMatch;

/**
 * AND group of permission patterns - all patterns within this group must match.
 */
type AndGroup = NonEmptyArray<PermissionPattern>;

/**
 * Boolean composition of permission patterns using arrays:
 * - Top-level array: OR across items
 * - Inner array: AND across items within that group
 *
 * @example
 * ```ts
 * ['OrgUsersRead', 'OrgUsersInvite'] // OrgUsersRead OR OrgUsersInvite
 * [['OrgUsersRead', '!OrgUsersDelete']] // OrgUsersRead AND NOT OrgUsersDelete
 * [['OrgUsersRead', '!OrgUsersDelete'], // (OrgUsersRead AND NOT OrgUsersDelete)
 *  ['OrgUsersInvite', '!OrgUsersRoleUpdate']] // OR (OrgUsersInvite AND NOT OrgUsersRoleUpdate)
 * ```
 */
export type HasPermissionType = NonEmptyArray<PermissionPattern | AndGroup>;

export interface HasBuildingPermissionOptions {
    buildingId?: string;
    organisationId?: string;
    userId?: string;
}

/**
 * Permission-based access utility.
 *
 * Semantics
 * - OR across top-level array items
 * - AND within any inner arrays
 * - Negations support exact matching:
 *   - `!X` => NOT exact match of `X`
 * - Flat arrays of only negatives use OR semantics; wrap in an inner array for AND-of-negatives
 * - Exact and exact-negated only; case-sensitive at runtime
 * - No admin override
 *
 * Guidance
 * - Prefer additive patterns over reductive ones for clarity and safety.
 * - Use canonical `PermissionKey` values for consistency
 *
 * Examples
 * ```ts
 * hasPermission(user, ['OrgUsersRead']); // Exact match
 * hasPermission(user, ['OrgUsersRead', 'OrgUsersInvite']); // OR: OrgUsersRead OR OrgUsersInvite
 * hasPermission(user, ['!OrgUsersDelete']); // NOT exactly OrgUsersDelete
 * hasPermission(user, [['OrgUsersRead', '!OrgUsersDelete']]); // AND: OrgUsersRead AND NOT OrgUsersDelete
 * hasPermission(user, [
 *   ['OrgUsersRead', '!OrgUsersDelete'],
 *   ['OrgUsersInvite', '!OrgUsersRoleUpdate'],
 * ]); // (OrgUsersRead AND NOT OrgUsersDelete) OR (OrgUsersInvite AND NOT OrgUsersRoleUpdate)
 * // For AND-of-negatives use an inner array:
 * hasPermission(user, [['!OrgUsersDelete', '!OrgUsersRoleUpdate']]); // NOT OrgUsersDelete AND NOT OrgUsersRoleUpdate
 * ```
 */
export function hasOrgPermission(
    /** The user object with a `permissions` array, or a raw permissions array */
    userOrPermissions: Maybe<{ permissions?: string[] } | string[]>,
    /** Permission patterns. Top-level OR, inner-array AND. Supports exact and negation (`!X`). */
    permissions: HasPermissionType
): boolean {
    const userPermissions = Array.isArray(userOrPermissions)
        ? userOrPermissions
        : (userOrPermissions?.permissions ?? []);

    if (userPermissions.length === 0 || permissions.length === 0) return false;

    return _hasPermission(userPermissions, permissions);
}

export function hasBuildingPermission(
    permission: Maybe<CurrentUserBuildingPermissionResponse>,
    permissions: HasPermissionType,
    options: HasBuildingPermissionOptions = {}
): boolean {
    if (!permission) {
        return false;
    }

    if (options.buildingId && permission.buildingId !== options.buildingId) {
        return false;
    }

    if (
        options.organisationId &&
        permission.organisationId !== options.organisationId
    ) {
        return false;
    }

    if (options.userId && permission.userId !== options.userId) {
        return false;
    }

    return _hasPermission(permission.permissions, permissions);
}

/**
 * @deprecated Use `hasOrgPermission` for `/me` permissions or
 * `hasBuildingPermission` for building-scoped permissions.
 */
export function hasPermission(
    userOrPermissions: Maybe<{ permissions?: string[] } | string[]>,
    permissions: HasPermissionType
): boolean {
    return hasOrgPermission(userOrPermissions, permissions);
}

export function _hasPermission(
    userPermissions: string[],
    permissions: HasPermissionType
): boolean {
    const matchPattern = (pattern: string): boolean => {
        if (!pattern) return false;

        if (pattern.startsWith('!')) {
            return !userPermissions.includes(pattern.substring(1));
        }

        return userPermissions.includes(pattern);
    };

    return permissions.some((clause) => {
        if (Array.isArray(clause)) {
            return clause.every((pattern) => matchPattern(pattern));
        }

        return matchPattern(clause);
    });
}
